import { Box, Skeleton, Stack, useTheme } from '@mui/material';
import { range } from 'lodash';
import { PropsWithChildren, useCallback, useMemo } from 'react';
import { SubmitHandler, useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { useReportError } from '@/hooks/useReportError';
import { useAssetProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { useUpdateEntityMutation } from '@/modules/entities/graphql/UpdateEntity.generated';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { StructuredAssetsSubform } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform';
import { STRUCTURED_ASSETS_SUBFORM_NAMESPACE } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  decodeSitusKind,
  filterEmptyStructuredAssetRows,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import {
  getAssetTaxSitusEventAction,
  useTrackAssetTaxSitusEvent,
} from '@/modules/entities/StructuredAssetsSubform/useTrackAssetTaxSitusEvent';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityPrincipalClientIds } from '@/modules/entities/utils/getEntityPrincipalClientIds';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { EntityLiquidityStatus, EntityStage } from '@/types/schema';

import { AssetIntegrationField } from '../AssetIntegrationField/AssetIntegrationField';
import { BalanceSheetConfigurationCard } from './BalanceSheetConfigurationCard';
import { DiscountEntityCard } from './DiscountEntityCard';
import {
  FullScreenStructuredAssetsModalForm,
  FullScreenStructuredAssetsModalFormPath,
} from './FullScreenStructuredAssetsModal.types';
import { createUpdateEntityAccountInput } from './FullScreenStructuredAssetsModal.utils';
import { useFullScreenStructuredAssetFormData } from './hooks/useFullScreenStructuredAssetFormData';
import { useValuationIntegrationDetails } from './hooks/useValuationLinkDetails';
import { IntegrationContextBanner } from './IntegrationContextBanner';
import { IntegrationValuationSummary } from './IntegrationValuationSummary';
import { LayoutWithSidePanel } from './LayoutWithSidePanel';
import { LiquidityAssumptionCard } from './LiquidityAssumptionCard';
import { TaxExposureCard } from './TaxExposureCard';
import { ValuationDateField } from './ValuationDateField';

interface FullScreenStructuredAssetsModelProps {
  householdId: string;
  entityType: EntityType;
  entityName: string;
  entityId: string;
  subtypeId: string;
  isOpen: boolean;
  onClose: () => void;
  headerText?: string;
  submitText?: string;
}

const {
  FormComponent: AssetsFormComponent,
  defaultValues: assetsFormDefaultValues,
} = new StructuredAssetsSubform();

const initialFormValues: FullScreenStructuredAssetsModalForm = {
  [STRUCTURED_ASSETS_SUBFORM_NAMESPACE]: assetsFormDefaultValues,
  valuationAsOfDate: null,
  _previousIntegrationEntityIds: [],
  integrationEntityIds: [],
  linkToAllAddeparEntities: false,
  allowAdditionalManualAssets: false,
  balanceSheetCategoryOverrideId: '',
  showBalanceSheetCategoryOverrideChooser: false,
  showEntityNameOnBalanceSheet: false,
  showCurrentDiscountedValueOnBalanceSheet: false,
  currentDiscountedValuePercentage: null,
  showAllIntegratedHoldings: false,
  pauseIntegrationSync: false,
  liquidityStatus: EntityLiquidityStatus.AssetBased,
  enableRealTppTaxExposure: false,
};

function FullScreenStructuredAssetsModalInner({
  entityId,
  subtypeId,
  householdId,
  entityType,
  headerText = 'Update asset details',
  submitText = 'Update asset details',
  entityName,
  isOpen,
  onClose,
}: FullScreenStructuredAssetsModelProps) {
  const theme = useTheme();
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const { isIntegratedScenario } = useValuationIntegrationDetails();
  const { householdIntegrationLink } = useHouseholdDetailsContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [updateEntity] = useUpdateEntityMutation({
    onCompleted: () => {
      showFeedback('New valuation saved', { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      showFeedback(
        'Failed to update asset details. Please refresh the page and try again.'
      );
      reportError('could not update structured asset details', err);
    },
  });

  const {
    handleSubmit: handleFormSubmit,
    reset,
    formState: { isSubmitting },
    control,
  } = useFormContext<FullScreenStructuredAssetsModalForm>();
  const {
    entity,
    valuation,
    loading: loadingFormData,
    balanceSheetCategories,
  } = useFullScreenStructuredAssetFormData(entityId, onClose);
  const ownerPrincipalIds = useMemo(
    () => getEntityPrincipalClientIds(entity),
    [entity]
  );
  const designerAccountId = entity?.subtype.designerAccount?.id ?? null;

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      reset(initialFormValues);
    }, theme.transitions.duration.leavingScreen);
  }, [onClose, reset, theme.transitions.duration.leavingScreen]);

  const trackAssetTaxSitusEvent = useTrackAssetTaxSitusEvent();
  const { assetClassesById } = useTenantDetailsContext();

  const onValidSubmission: SubmitHandler<
    FullScreenStructuredAssetsModalForm
  > = async (values) => {
    const updateEntityAccountInput = createUpdateEntityAccountInput(values, {
      activeClientIntegrationKind: householdIntegrationLink,
      previousValuation: valuation,
      entityId,
      entityType,
      subtypeId,
      householdId,
      // Silently activate PreCreated entities on submit, matching the
      // Add Holdings modal pattern. The user may reach this modal before
      // ever interacting with Add Holdings.
      stage:
        entity?.stage === EntityStage.PreCreated
          ? EntityStage.Active
          : undefined,
    });

    const submittedAssets = filterEmptyStructuredAssetRows(
      values.structuredAssetsSubform?.assets
    );

    const result = await updateEntity({
      variables: {
        input: updateEntityAccountInput,
      },
    });
    if (result.errors) return;

    for (const asset of submittedAssets) {
      const action = getAssetTaxSitusEventAction(
        asset._originalTaxSitus,
        asset.taxSitus
      );
      if (action) {
        trackAssetTaxSitusEvent(action, {
          source: 'structured_assets_modal',
          householdId,
          entityId,
          assetClass: assetClassesById[asset.categoryId]?.displayName,
          situsKind: decodeSitusKind(asset.taxSitus),
          entityType,
        });
      }
    }
  };
  const handleSubmit = handleFormSubmit(onValidSubmission);
  const formAssetsSum = useWatch({
    control,
    name: 'structuredAssetsSubform._assetsValueSum',
  });

  return (
    <FullScreenModal isOpen={isOpen}>
      {isOpen && (
        <HouseholdPermissionAwareDisabledProvider>
          <Stack component="form" noValidate onSubmit={handleSubmit}>
            <LayoutWithSidePanel
              heading={headerText}
              subheading={entityName}
              MainContent={
                loadingFormData ? (
                  <LoadingSkeleton />
                ) : (
                  <MainContentComponent
                    valuation={valuation}
                    householdId={householdId}
                    entityId={entityId}
                    entityType={entityType}
                    subtypeId={subtypeId}
                    designerAccountId={designerAccountId}
                    ownerPrincipalIds={ownerPrincipalIds}
                  />
                )
              }
              SidebarContent={
                <Stack justifyContent="space-between" height="100%">
                  <Stack spacing={2} sx={{ p: 2 }}>
                    <AssetIntegrationField
                      entityType={entityType}
                      householdId={householdId}
                      assetsSum={formAssetsSum}
                    />
                    {(isIntegratedScenario ||
                      !!entity?.integrationEntities?.length ||
                      entity?.pauseIntegrationSync) && (
                      <FormAwareSwitch
                        control={control}
                        fieldName="pauseIntegrationSync"
                        label="Pause integration syncing"
                        labelPosition="right"
                        disabled={
                          !canManageIntegrationData ||
                          loadingFormData ||
                          isSubmitting
                        }
                        helpText="Pause updates without disconnecting the integration."
                      />
                    )}
                    <DiscountEntityCard />
                    <BalanceSheetConfigurationCard
                      balanceSheetCategories={balanceSheetCategories}
                    />
                    {BUSINESS_ENTITY_TYPES.includes(entityType) && (
                      <LiquidityAssumptionCard />
                    )}
                    {BUSINESS_ENTITY_TYPES.includes(entityType) && (
                      <TaxExposureCard />
                    )}
                  </Stack>
                  <ValuationDateField valuation={valuation} />
                </Stack>
              }
              Footer={
                <Footer
                  rightAction={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      spacing={1}
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleClose}
                      >
                        {canEdit ? 'Cancel' : 'Close'}
                      </Button>
                      {canEdit && (
                        <Button
                          loading={isSubmitting}
                          size="sm"
                          variant="primary"
                          onClick={handleSubmit}
                        >
                          {submitText}
                        </Button>
                      )}
                    </Stack>
                  }
                />
              }
            />
          </Stack>
        </HouseholdPermissionAwareDisabledProvider>
      )}
    </FullScreenModal>
  );
}

export function FullScreenStructuredAssetsModal(
  props: FullScreenStructuredAssetsModelProps
) {
  const formMethods = useForm<FullScreenStructuredAssetsModalForm>({
    defaultValues: initialFormValues,
  });
  return (
    <FormProvider {...formMethods}>
      <FullScreenStructuredAssetsModalInner {...props} />
    </FormProvider>
  );
}

interface MainContentComponentProps {
  householdId: string;
  entityId: string;
  entityType: EntityType;
  subtypeId: string;
  designerAccountId: string | null;
  valuation: StructuredAssetsSubform_ValuationFragment | null;
  ownerPrincipalIds: string[];
}

function MainContentComponent({
  valuation,
  householdId,
  entityId,
  entityType,
  subtypeId,
  designerAccountId,
  ownerPrincipalIds,
}: MainContentComponentProps) {
  const { isIntegratedScenario: isLinkedToIntegration } =
    useValuationIntegrationDetails();
  const integrationDisplayName = useAssetProviderDisplayName({
    isInline: true,
  });
  const { control } = useFormContext<FullScreenStructuredAssetsModalForm>();
  const allowAdditionalManualAssets = useWatch({
    control: control,
    name: `allowAdditionalManualAssets` as const satisfies FullScreenStructuredAssetsModalFormPath,
  });

  const shouldRenderAssetFormComponent = (() => {
    if (isLinkedToIntegration && !allowAdditionalManualAssets) return false;
    return true;
  })();

  const assetFormComponent = shouldRenderAssetFormComponent && (
    <AssetFormComponentWrapper>
      <AssetsFormComponent
        hideDescriptionInput={isLinkedToIntegration}
        entityId={entityId}
        entityType={entityType}
        householdId={householdId}
        ownerPrincipalIds={ownerPrincipalIds}
      />
    </AssetFormComponentWrapper>
  );

  return (
    <Stack spacing={2}>
      {isLinkedToIntegration && allowAdditionalManualAssets && (
        <IntegrationContextBanner variant="linked-with-manual" mb={2} />
      )}
      {assetFormComponent}
      {isLinkedToIntegration && (
        <>
          {!allowAdditionalManualAssets && (
            <IntegrationContextBanner variant="linked" mb={2} />
          )}
          <Box>
            <TableHeaderBar
              header={`Assets tracked in ${integrationDisplayName}`}
            />
            <IntegrationValuationSummary
              valuation={valuation}
              entityId={entityId}
              entityType={entityType}
              subtypeId={subtypeId}
              designerAccountId={designerAccountId}
              ownerPrincipalIds={ownerPrincipalIds}
            />
          </Box>
        </>
      )}
    </Stack>
  );
}

function LoadingSkeleton() {
  const rows = range(5);

  return (
    <Stack p={2}>
      <Stack spacing={1} direction="row">
        <Skeleton component="div" sx={{ width: '33%' }} height={50} />
      </Stack>
      {rows.map((row) => (
        <Stack key={row} spacing={1} direction="row">
          <Skeleton component="div" sx={{ width: '75%' }} height={50} />
          <Skeleton component="div" sx={{ flexBasis: '25%' }} height={50} />
        </Stack>
      ))}
    </Stack>
  );
}

function AssetFormComponentWrapper({ children }: PropsWithChildren<unknown>) {
  return (
    <Box>
      <TableHeaderBar header="Manually tracked assets" />
      <Card
        variant="filled"
        sx={{ p: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      >
        {children}
      </Card>
    </Box>
  );
}
